import React, { useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { contentEs } from '../data/contentEs.js';
import { contentEn } from '../data/contentEn.js';
import { LocationsInteractiveDirectory } from '../components/LocationsInteractiveDirectory.js';

interface LocationsPageProps {
  isEn?: boolean;
}

export const LocationsPage: React.FC<LocationsPageProps> = ({ isEn = false }) => {
  const c = isEn ? contentEn : contentEs;
  const l = c.b2c;

  useEffect(() => {
    document.title = c.meta.locations.title;
  }, [c.meta.locations.title]);

  return (
    <div className="flex flex-col bg-[#FBF9F5]">
      {/* Hero Section with Full-Bleed Background Image */}
      <section className="relative min-h-[70vh] lg:min-h-[78vh] flex items-center justify-center overflow-hidden bg-stone-900 border-b border-[#E8E4DC]">
        {/* Background Image with Luminous Warm Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/website/sucursales-hero-familia.png"
            alt="Familia disfrutando en sucursal Doña Blanca"
            className="w-full h-full object-cover object-center filter brightness-[0.98] contrast-[1.03] scale-[1.01]"
            loading="eager"
          />
          {/* Subtle gradient overlay: warm, luminous yet high contrast for text */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/45 to-black/30 sm:bg-gradient-to-r sm:from-black/80 sm:via-black/55 sm:to-black/25" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 w-full">
          <div className="max-w-2xl sm:max-w-3xl space-y-6">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#EAB308]/90 text-[#001557] text-xs font-black uppercase tracking-wider backdrop-blur-sm shadow-md">
              {l.hero.eyebrow}
            </span>

            <h1 className="font-heading font-black text-3xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight drop-shadow-[0_4px_16px_rgba(0,0,0,0.7)]">
              <span>{l.hero.title} </span>
              <span className="text-[#38BDF8] block sm:inline">{l.hero.titleAccent}</span>
            </h1>

            <p className="text-base sm:text-xl text-gray-100 font-medium leading-relaxed max-w-2xl drop-shadow-[0_2px_10px_rgba(0,0,0,0.85)]">
              {l.hero.body}
            </p>

            <div className="pt-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href="#directorio"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-[#EAB308] hover:bg-[#D99B00] text-[#001557] font-bold text-base px-8 py-4 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-200 border border-[#CA8A04]/50 hover:scale-[1.02]"
              >
                <span>{l.hero.cta}</span>
                <ArrowRight className="w-5 h-5 text-[#001557]" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Flavor Section */}
      <section className="py-20 lg:py-24 bg-[#FAF8F5] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl">
            <span className="text-xs uppercase font-extrabold tracking-widest text-[#97472E]">
              {l.flavor.eyebrow}
            </span>
            <h2 className="mt-2 font-heading font-black text-3xl sm:text-4xl text-[#001557] tracking-tight">
              <span>{l.flavor.title} </span>
              <span className="text-[#0284C7]">{l.flavor.titleAccent}</span>
            </h2>
            <p className="mt-3 text-base text-gray-600 leading-relaxed">
              {l.flavor.body}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl border border-[#E5E0D8] overflow-hidden shadow-sm hover:shadow-md transition-shadow group flex flex-col justify-between">
              <div>
                <div className="h-52 overflow-hidden bg-amber-50">
                  <img
                    src="/website/food-desayuno.png"
                    alt="Desayunos Doña Blanca"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 px-2.5 py-1 rounded">
                    {l.flavor.cards[0].tag}
                  </span>
                  <h3 className="font-heading font-black text-xl text-[#001557] mt-3">
                    {l.flavor.cards[0].title}
                  </h3>
                  <p className="mt-2 text-xs text-gray-600 leading-relaxed">
                    {l.flavor.cards[0].desc}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#E5E0D8] overflow-hidden shadow-sm hover:shadow-md transition-shadow group flex flex-col justify-between">
              <div>
                <div className="h-52 overflow-hidden bg-amber-50">
                  <img
                    src="/website/food-comida.png"
                    alt="Comida mexicana tradicional"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded">
                    {l.flavor.cards[1].tag}
                  </span>
                  <h3 className="font-heading font-black text-xl text-[#001557] mt-3">
                    {l.flavor.cards[1].title}
                  </h3>
                  <p className="mt-2 text-xs text-gray-600 leading-relaxed">
                    {l.flavor.cards[1].desc}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#E5E0D8] overflow-hidden shadow-sm hover:shadow-md transition-shadow group flex flex-col justify-between">
              <div>
                <div className="h-52 overflow-hidden bg-amber-50">
                  <img
                    src="/website/food-pan.png"
                    alt="Pan y postres artesanales"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-orange-700 bg-orange-50 px-2.5 py-1 rounded">
                    {l.flavor.cards[2].tag}
                  </span>
                  <h3 className="font-heading font-black text-xl text-[#001557] mt-3">
                    {l.flavor.cards[2].title}
                  </h3>
                  <p className="mt-2 text-xs text-gray-600 leading-relaxed">
                    {l.flavor.cards[2].desc}
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* History Section */}
      <section className="py-20 bg-white border-b border-[#E8E4DC]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <span className="text-xs uppercase font-extrabold tracking-widest text-[#001557]">
            {l.history.eyebrow}
          </span>
          <h2 className="font-heading font-black text-2xl sm:text-4xl text-[#001557]">
            {l.history.title}
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            {l.history.body}
          </p>
        </div>
      </section>

      {/* Modern Interactive CDMX Directory Section */}
      <section id="directorio" className="py-20 lg:py-28 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mb-10">
            <span className="text-xs uppercase font-extrabold tracking-widest text-[#001557]">
              {l.directory.eyebrow}
            </span>
            <h2 className="mt-2 font-heading font-black text-3xl sm:text-4xl text-[#001557] tracking-tight">
              {l.directory.title}
            </h2>
            <p className="mt-3 text-base text-gray-600 leading-relaxed">
              {isEn 
                ? 'Explore our 13 strategic locations across Mexico City. Select any branch to see exact hours, get directions or order delivery, and explore their geographic position on our interactive CDMX map.' 
                : 'Explora nuestros 13 puntos estratégicos en la Ciudad de México. Selecciona cualquier sucursal para desplegar horarios, cómo llegar o pedir a domicilio, y explora su ubicación geográfica en nuestro mapa interactivo de la CDMX.'}
            </p>
          </div>

          {/* Interactive Compact Directory with CDMX Visual Map Explorer */}
          <LocationsInteractiveDirectory isEn={isEn} />

        </div>
      </section>

    </div>
  );
};
